import React, { useState, useEffect, useRef } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { Gem, Sparkles, Heart, Wind, Sun, Shield, CalendarCheck, Clock, Loader2, Tag, ChevronLeft, ChevronRight } from 'lucide-react';

const backendUrl = process.env.REACT_APP_BACKEND_URL || '';

const BENEFIT_ICONS = [Gem, Heart, Sparkles, Wind, Sun, Shield, CalendarCheck, Tag];

const CrystalHealingSection = ({ onRegisterClick }) => {
  const { isRTL } = useLanguage();
  const carouselRef = useRef(null);

  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSessions = async () => {
      setLoading(true);
      try {
        const res = await fetch(`${backendUrl}/api/cms/sessions`);
        if (res.ok) {
          const data = await res.json();
          const published = Array.isArray(data)
            ? data.filter(s => s.status === 'published')
            : [];
          setSessions(published);
        }
      } catch (err) {
        console.error('Failed to load sessions:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchSessions();
  }, []);

  const getTitle = (s) => isRTL ? (s.arabic_title || s.title || '') : (s.title || '');
  const getDescription = (s) => isRTL ? (s.description_ar || s.description || '') : (s.description || '');
  const getBenefits = (s) => isRTL ? (s.benefits_ar?.length ? s.benefits_ar : s.benefits) : s.benefits;
  const getExpectations = (s) => {
    if (s.expectations && s.expectations.length > 0) return s.expectations;
    return getBenefits(s) || [];
  };
  const getImageSrc = (url) => (url?.startsWith('/') ? `${backendUrl}${url}` : url);

  const formatDuration = (mins) => {
    if (!mins) return null;
    if (isRTL) return `${mins} دقيقة`;
    return mins >= 60
      ? `${Math.floor(mins / 60)}h${mins % 60 ? ` ${mins % 60}m` : ''}`
      : `${mins} min`;
  };

  const formatPrice = (price, currency) => {
    if (!price) return null;
    return `${price} ${currency || 'EGP'}`;
  };

  const scrollCarousel = (dir) => {
    if (!carouselRef.current) return;
    const amount = carouselRef.current.offsetWidth * 0.75;
    carouselRef.current.scrollBy({ left: dir === 'next' ? amount : -amount, behavior: 'smooth' });
  };

  const featuredSession = sessions.find(s => s.is_featured) || sessions[0] || null;
  const carouselSessions = sessions.filter(s => s !== featuredSession);

  return (
    <section id="crystal-healing" className="section-padding bg-charcoal relative overflow-hidden">
      <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-gold/30 to-transparent" />
      <div className="absolute bottom-0 right-0 w-64 sm:w-96 h-64 sm:h-96 bg-gold/5 rounded-full blur-3xl -translate-y-1/2 hidden sm:block" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-10 relative z-10">

        {/* Section header */}
        <div className="text-center max-w-3xl mx-auto mb-10 sm:mb-16">
          <p className="text-gold text-xs uppercase tracking-[0.2em] sm:tracking-[0.3em] mb-3 sm:mb-4 font-medium">
            {isRTL ? 'جلسات الشفاء' : 'Healing Sessions'}
          </p>
          <h2 className="font-serif text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl text-gold leading-tight mb-4 sm:mb-6 px-2">
            {isRTL ? 'تجاربنا المميّزة' : 'Our Signature Experiences'}
          </h2>
          <div className="gold-divider" />
          <p className="text-cream text-base sm:text-lg lg:text-xl mt-6 sm:mt-8 leading-relaxed font-light px-2">
            {isRTL
              ? 'جلسات مصمَّمة بعناية لاستعادة التوازن وتغذية الروح والعقل والجسد.'
              : 'Carefully designed experiences to restore balance and nourish the mind, body, and spirit.'}
          </p>
        </div>

        {/* Loading */}
        {loading && (
          <div className="flex justify-center items-center py-20">
            <Loader2 className="w-7 h-7 text-gold animate-spin" />
          </div>
        )}

        {/* Empty state */}
        {!loading && sessions.length === 0 && (
          <div className="text-center py-16 text-cream-400 bg-charcoal-100 border border-white/10 rounded-xl max-w-lg mx-auto">
            {isRTL ? 'لا توجد جلسات متاحة حالياً.' : 'No active sessions available at the moment.'}
          </div>
        )}

        {/* Featured hero session */}
        {!loading && featuredSession && (() => {
          const s = featuredSession;
          const title = getTitle(s);
          const description = getDescription(s);
          const expectations = getExpectations(s);
          const duration = formatDuration(s.duration_minutes);
          const price = formatPrice(s.price, s.currency);
          const imageSrc = getImageSrc(s.image_url);

          return (
            <div className="mb-16 sm:mb-20">
              {/* Hero image */}
              {imageSrc && (
                <div className="max-w-4xl mx-auto mb-10 sm:mb-14">
                  <div className="relative rounded-xl overflow-hidden">
                    <img
                      src={imageSrc}
                      alt={title}
                      className="w-full h-64 sm:h-80 lg:h-96 object-cover"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-charcoal/60 via-transparent to-transparent" />
                    <div className="absolute bottom-0 left-0 right-0 px-6 py-5">
                      <div className="flex flex-wrap items-center gap-3">
                        {duration && (
                          <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-charcoal/80 border border-gold/30 text-gold text-xs rounded-full">
                            <Clock size={11} />
                            {duration}
                          </span>
                        )}
                        {price && (
                          <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-charcoal/80 border border-gold/30 text-gold text-xs rounded-full">
                            <Tag size={11} />
                            {price}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Content grid */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 xl:gap-20 items-start">
                {/* Description side */}
                <div className={isRTL ? 'lg:order-2' : 'lg:order-1'}>
                  <h3 className="font-serif text-2xl sm:text-3xl lg:text-4xl text-cream mb-5 leading-snug">
                    {title}
                  </h3>

                  {!imageSrc && (duration || price) && (
                    <div className="flex flex-wrap gap-3 mb-5">
                      {duration && (
                        <span className="inline-flex items-center gap-1.5 px-3 py-1 border border-gold/30 bg-gold/5 text-gold text-xs rounded-full">
                          <Clock size={11} />
                          {duration}
                        </span>
                      )}
                      {price && (
                        <span className="inline-flex items-center gap-1.5 px-3 py-1 border border-gold/30 bg-gold/5 text-gold text-xs rounded-full">
                          <Tag size={11} />
                          {price}
                        </span>
                      )}
                    </div>
                  )}

                  <div className="space-y-5">
                    <p className="text-cream text-sm sm:text-base lg:text-lg leading-relaxed font-light">
                      {description}
                    </p>
                    {description && (
                      <div className="bg-gold/[0.06] border border-gold/20 rounded-xl px-5 py-4 sm:px-6 sm:py-5">
                        <p className="text-cream-200 text-sm sm:text-base leading-relaxed italic">
                          {isRTL
                            ? 'كل جلسة هي خطوة نحو توازن أعمق وسلام داخلي حقيقي.'
                            : 'Each session is a step toward deeper balance and lasting inner peace.'}
                        </p>
                      </div>
                    )}
                  </div>

                  <div className="mt-8">
                    <button
                      onClick={() => onRegisterClick && onRegisterClick(s.session_type || s.slug)}
                      className="btn-gold inline-flex items-center gap-2 sm:gap-3 glow-yellow w-full sm:w-auto justify-center"
                    >
                      <CalendarCheck size={18} />
                      <span>{isRTL ? 'احجزي جلستك' : 'Book Your Session'}</span>
                    </button>
                  </div>
                </div>

                {/* Expectations side */}
                {expectations.length > 0 && (
                  <div className={isRTL ? 'lg:order-1' : 'lg:order-2'}>
                    <div className="p-5 sm:p-8 lg:p-10 border border-gold/20 bg-charcoal-100 rounded-xl relative">
                      <div className="absolute top-0 left-0 w-10 h-10 sm:w-16 sm:h-16 border-t-2 border-l-2 border-gold" />
                      <div className="absolute bottom-0 right-0 w-10 h-10 sm:w-16 sm:h-16 border-b-2 border-r-2 border-gold" />

                      <h4 className="text-gold text-xs sm:text-sm uppercase tracking-[0.15em] sm:tracking-[0.2em] mb-6 sm:mb-8 text-center">
                        {isRTL ? 'ما يمكنك توقعه' : 'What you can expect'}
                      </h4>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
                        {expectations.map((item, bIdx) => {
                          const Icon = BENEFIT_ICONS[bIdx % BENEFIT_ICONS.length];
                          return (
                            <div key={bIdx} className="flex items-center gap-3 sm:gap-4 group">
                              <div className="w-10 h-10 sm:w-11 sm:h-11 flex items-center justify-center rounded-lg border border-gold/30 bg-gold/5 group-hover:bg-gold/10 transition-colors flex-shrink-0">
                                <Icon size={17} className="text-gold" />
                              </div>
                              <span className="text-cream text-xs sm:text-sm leading-snug">{item}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        })()}

        {/* Carousel — remaining sessions */}
        {!loading && carouselSessions.length > 0 && (
          <div className="pt-6 sm:pt-8 border-t border-gold/10">
            <div className="flex items-center justify-between mb-6 sm:mb-8">
              <h3 className="font-serif text-xl sm:text-2xl text-cream">
                {isRTL ? 'جلسات أخرى' : 'More Sessions'}
              </h3>
              <div className="flex gap-2">
                <button
                  onClick={() => scrollCarousel('prev')}
                  className="w-9 h-9 flex items-center justify-center rounded-full border border-gold/30 text-gold hover:bg-gold/10 transition-colors"
                  aria-label="Previous"
                >
                  <ChevronLeft size={18} />
                </button>
                <button
                  onClick={() => scrollCarousel('next')}
                  className="w-9 h-9 flex items-center justify-center rounded-full border border-gold/30 text-gold hover:bg-gold/10 transition-colors"
                  aria-label="Next"
                >
                  <ChevronRight size={18} />
                </button>
              </div>
            </div>

            <div
              ref={carouselRef}
              className="flex gap-5 overflow-x-auto scroll-smooth pb-4"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
              {carouselSessions.map((s, idx) => {
                const title = getTitle(s);
                const description = getDescription(s);
                const duration = formatDuration(s.duration_minutes);
                const price = formatPrice(s.price, s.currency);
                const imageSrc = getImageSrc(s.image_url);

                return (
                  <div
                    key={s.id || s.slug || idx}
                    className="flex-shrink-0 w-72 sm:w-80 bg-charcoal-100 border border-white/10 rounded-xl overflow-hidden hover:border-gold/30 transition-colors group"
                  >
                    {imageSrc ? (
                      <div className="relative h-44 overflow-hidden">
                        <img
                          src={imageSrc}
                          alt={title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          loading="lazy"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-charcoal/50 to-transparent" />
                      </div>
                    ) : (
                      <div className="h-44 bg-gold/5 flex items-center justify-center">
                        <Gem size={32} className="text-gold/30" />
                      </div>
                    )}

                    <div className="p-5">
                      <h4 className="font-serif text-lg text-cream mb-2 leading-snug line-clamp-2">{title}</h4>
                      <p className="text-cream-400 text-xs leading-relaxed line-clamp-2 mb-4">{description}</p>

                      <div className="flex flex-wrap gap-2 mb-4">
                        {duration && (
                          <span className="inline-flex items-center gap-1 px-2.5 py-1 border border-gold/25 bg-gold/5 text-gold text-xs rounded-full">
                            <Clock size={10} />
                            {duration}
                          </span>
                        )}
                        {price && (
                          <span className="inline-flex items-center gap-1 px-2.5 py-1 border border-gold/25 bg-gold/5 text-gold text-xs rounded-full">
                            <Tag size={10} />
                            {price}
                          </span>
                        )}
                      </div>

                      <button
                        onClick={() => onRegisterClick && onRegisterClick(s.session_type || s.slug)}
                        className="w-full py-2.5 border border-gold/40 text-gold text-sm rounded-lg hover:bg-gold/10 transition-colors flex items-center justify-center gap-2"
                      >
                        <CalendarCheck size={14} />
                        {isRTL ? 'احجزي' : 'Book Now'}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

      </div>
    </section>
  );
};

export default CrystalHealingSection;
